import { Box, Button, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { getMaskedValue } from "components/elements/MaskedModeWrapper";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { IInvestmentChart } from "types/types";
import { percentage } from "utils";
import { renderNumber } from "utils/formatter";

import { TargetSlider } from "./styles";

dayjs.extend(isToday);

interface Props {
  chartData: IInvestmentChart;
  baseCurrency: string;
  baseCurrDisplayedScale: number;
  baseCurrShortDisplayedScale: number;
  baseCurrencyAmountTargets: number[];
  isFullScreen?: boolean;
  onEditTargets?: () => void;
}

const BaseCurrencyAmountChart = ({
  chartData,
  baseCurrency,
  baseCurrDisplayedScale,
  baseCurrShortDisplayedScale,
  baseCurrencyAmountTargets,
  isFullScreen,
  onEditTargets,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMasked } = useUserDataContext();
  const { colors } = useMUIThemeModeContext();

  const labels = chartData.points.map((item) =>
    Intl.DateTimeFormat("cs-CZ").format(new Date(dayjs(item.d).format()))
  );

  const series = [
    {
      name: t("chartHistory.baseCurrencyAmountChart.amount"),
      data: chartData.points.map((item) => item.a),
    },
  ];

  const minAmount = Math.min(...chartData.points.map((item) => item.a));
  const maxAmount = Math.max(...chartData.points.map((item) => item.a));
  const currentAmount = chartData.points[chartData.points.length - 1]?.a ?? 0;

  const sortedTargets = [...baseCurrencyAmountTargets].sort((a, b) => a - b);
  const nextTarget = sortedTargets.find((t) => t > currentAmount);
  const highestTarget = sortedTargets[sortedTargets.length - 1];
  const hasTargets = baseCurrencyAmountTargets.length > 0;

  let yMax: number | undefined;
  if (isFullScreen) {
    yMax = highestTarget !== undefined ? Math.max(maxAmount, highestTarget) : undefined;
  } else {
    yMax = nextTarget !== undefined ? Math.max(maxAmount, nextTarget) : undefined;
  }

  // chart config
  const options: ApexOptions = {
    theme: { mode: theme.palette.mode },
    legend: {
      show: false,
    },
    tooltip: {
      followCursor: true,
      y: {
        formatter: (val: number) => getMaskedValue(isMasked, renderNumber(val, baseCurrDisplayedScale, true)),
      },
    },
    colors: [colors.black],
    chart: {
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      background: "transparent",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "stepline",
      width: 3,
    },
    grid: {
      row: {
        colors: [colors.legacyGray100, "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        hideOverlappingLabels: true,
        rotate: -30,
        show: true,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      show: true,
      min: Math.min(0, minAmount),
      max: yMax,
      labels: {
        formatter: (val: any) => {
          return getMaskedValue(isMasked, renderNumber(val, baseCurrShortDisplayedScale, true));
        },
      },
    },
    annotations: {
      yaxis: baseCurrencyAmountTargets.map((target) => {
        const achieved = currentAmount >= target;
        const color = achieved ? colors.success : theme.palette.primary.main;
        const targetValue = getMaskedValue(isMasked, renderNumber(target, baseCurrDisplayedScale));

        let text;
        if (achieved) {
          text = `${targetValue} ${baseCurrency} ✔`;
        } else {
          const percentageValue = percentage(target, currentAmount, 2);
          text = t("chartHistory.baseCurrencyAmountChart.targetLabel", { percentageValue: renderNumber(percentageValue, 2), targetValue, baseCurrency });
        }

        return {
          y: target,
          borderColor: color,
          strokeDashArray: achieved ? 5 : 0,
          label: {
            text,
            offsetX: -10,
            style: {
              color: "#fff",
              background: color,
            },
          },
        };
      }),
    },
  };

  return (
    <Box sx={{ pt: { xs: 2 }, pr: { xs: 0, md: 2 }, textAlign: "center", height: "100%" }}>
      <ReactApexChart
        options={options}
        series={series}
        type="line"
        width={"100%"}
        height={isFullScreen ? "95%" : 400}
      />
      {!isFullScreen && (
        <Box sx={{ pt: 2, width: "100%" }}>
          {hasTargets && (
            <TargetSlider
              value={currentAmount}
              valueLabelDisplay={"on"}
              valueLabelFormat={(n: number) => `${getMaskedValue(isMasked, renderNumber(n, baseCurrDisplayedScale, true))} ${baseCurrency}`}
              min={0}
              max={Math.max(...baseCurrencyAmountTargets, currentAmount)}
              marks={[
                ...baseCurrencyAmountTargets.map((target) => ({
                  value: target,
                  label: getMaskedValue(isMasked, renderNumber(target, baseCurrDisplayedScale)),
                })),
              ]}
            />
          )}
          {onEditTargets && (
            <Button variant="outlined" size="small" color="info" onClick={onEditTargets} sx={{ mt: 2 }}>
              {hasTargets ? t("chartHistory.baseCurrencyAmountChart.edit", { baseCurrency }) : t("chartHistory.baseCurrencyAmountChart.set", { baseCurrency })}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BaseCurrencyAmountChart;
